/**
 * Shared upload constraints for the bill-document ingestion surfaces.
 *
 * Lives in `@orn/global` so the server-side endpoints and the client-side
 * picker UI / preflight check read from one source — drift between
 * SMS, the upload route, and the composer UI becomes a compile-time
 * impossibility.
 *
 * Two whitelists today, deliberately split:
 *
 *  - {@link BILL_UPLOAD_MIME_TYPES} — SMS MMS only. Narrow set (images +
 *    PDF) because the MMS carrier path is brittle on anything else.
 *  - {@link ASSISTANT_BILL_UPLOAD_MIME_TYPES} — assistant composer
 *    endpoint (`POST /api/assistant/upload`) + the client hook
 *    (`useUploadAttachment`). Broader set mirroring the inbound email
 *    attachment surface (PDF / common images / Office docs / CSV / SVG /
 *    HTML / text) so a user forwarding a bill through the composer has
 *    feature parity with forwarding it via email.
 *
 * Both narrower than the broader `DOCUMENT_MIME_TYPES` map in
 * `packages/api/src/utils/mimeTypes.ts` (which is the union plus a few
 * extra extensions); that file's set is for extension lookup only, not
 * an ingestion whitelist.
 */

export const BILL_UPLOAD_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
]);

export const ASSISTANT_BILL_UPLOAD_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/gif',
  'image/tiff',
  'image/bmp',
  'text/csv',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

/**
 * MIME types intentionally NOT on {@link ASSISTANT_BILL_UPLOAD_MIME_TYPES}
 * even though they appear in the email pipeline's whitelist:
 *
 * - `image/svg+xml`: XML payload that can carry inline `<script>` / `on*=`
 *   handlers. `file-type` cannot sniff XML, so a magic-number check has no
 *   teeth against it; if a future preview surface renders the file via
 *   `<object>`, `<iframe>`, or "open in new tab" the script executes in
 *   the S3 origin. Bills are never SVG in practice, so the format is
 *   dropped rather than gated behind a strict XML sanitizer.
 * - `text/html`: same `file-type`-blind shape as SVG, with the additional
 *   nightmare of arbitrary inline scripts. The email pipeline accepts
 *   HTML because the message body is HTML by spec; the assistant composer
 *   has no analogous reason.
 *
 * If a future use case genuinely needs either, add a format-specific
 * structural validator (XML sanitizer rejecting `<script>` / `on*=` /
 * `<foreignObject>` / `xlink:href="javascript:"`, HTML sanitizer with a
 * tight allowlist) and gate the whitelist on that validator passing.
 */

/**
 * Per-file size cap. Multi-page scanned bills + HEIC bursts from modern
 * phones routinely exceed 20 MB; 48 MB × {@link MAX_FILES_PER_REQUEST}
 * covers the realistic worst case without inviting outright abuse. The
 * client-side composer should mirror this cap so the user gets a local
 * "this file is too large" error instead of waiting for the server's 413.
 */
export const MAX_FILE_BYTES = 48 * 1024 * 1024;

/**
 * Per-file size cap specifically for text-family formats (`text/csv`,
 * `text/plain`). Bill text content (CSV statements, plain-text invoices)
 * is universally small; 48 MB of "text" is overwhelmingly likely to be
 * a binary payload mislabeled as text to bypass the sniff. Enforced
 * inside `validateMagicNumber` in addition to the multer per-file cap.
 */
export const MAX_TEXT_FILE_BYTES = 1 * 1024 * 1024;

/**
 * Maximum combined uncompressed size of any OOXML zip-container payload
 * (DOCX / XLSX). A 1 MB nested-deflate "zip bomb" decompresses to many
 * GB, which would OOM the LLM extractor (Gemini decompresses to read
 * cells / paragraphs) or run up billing. Enforced via
 * `validateZipDecompressedSize` when the OOXML ↔ ZIP alias path fires.
 */
export const MAX_ZIP_UNCOMPRESSED_BYTES = 200 * 1024 * 1024;

/**
 * Maximum entry count in any OOXML zip-container payload. Defends against
 * a "million tiny files" zip whose sum of uncompressed sizes stays under
 * {@link MAX_ZIP_UNCOMPRESSED_BYTES} but exhausts CPU during parsing.
 */
export const MAX_ZIP_ENTRY_COUNT = 1000;

/**
 * Max files per upload request. Bounds the worst-case request size at
 * `MAX_FILE_BYTES × MAX_FILES_PER_REQUEST` (~480 MB) and gives the
 * multi-page bill use case (a few pages plus margin) headroom without
 * unbounded growth. Composer UI should disable the "add more" button once
 * the picker holds this many files.
 */
export const MAX_FILES_PER_REQUEST = 10;
